import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { firebaseApp } from "../../firebase/app";
import Dialog from "../../components/dialog/dialog";
import { Button } from "../../components";
import { hasDashboardV2Access, redirectToDashboardV2 } from "../../utils/dashboard-v2";
import { isExtensionClient } from "../../utils/extension-bridge";
import { authSet, getOriginUid, getStaffUid, getUid, logout } from "../../redux/authSlice";
import { modeSet } from "../../redux/layoutSlice";
import { branchNameSet } from "../../redux/userSlice";
import { logoutUser } from "../../utils/logout";

// Shown on every dashboard load when the dealership has useDashboardV2
// enabled. Intentionally not dismissable — the only way out is the redirect
// or logging out.
export default function DashboardV2Modal() {
  // Inside the extension panel the extension performs the V1 → V2 move itself,
  // with a custom-token handoff so the user isn't asked to sign in again. A
  // blocking modal — and the introv2.drivee.ai page behind it — has no business
  // rendering in a ~400px panel. Bailing out here, before the inner component
  // mounts, also spares the RTDB feature-flag read the modal could never use.
  if (isExtensionClient()) return null;
  return <DashboardV2ModalContent />;
}

function DashboardV2ModalContent() {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dealerUid = useSelector(getUid);
  const staffUid = useSelector(getStaffUid);
  const originUid = useSelector(getOriginUid);

  useEffect(() => {
    let cancelled = false;

    const auth = getAuth(firebaseApp);
    const user = auth.currentUser;
    if (!user) return;

    user
      .getIdTokenResult()
      .then(async (result: any) => {
        // The flag lives on the dealership (manager) account: staff,
        // supervisors and every other role carry it in the managerUID claim.
        const dealerUid = (result?.claims?.managerUID as string) ?? user.uid;
        const enabled = await hasDashboardV2Access(dealerUid);
        if (!cancelled && enabled) setOpen(true);
      })
      .catch((error: any) => console.log(error, "error checking dashboard v2 access"));

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = async () => {
    // dealerUid is the branch/dealer, userUid is the actual user (staff or manager)
    const userUid = staffUid || originUid;

    try {
      await logoutUser(dealerUid, userUid);

      localStorage.removeItem("userPreferences");
      localStorage.removeItem("branchUid");
      localStorage.removeItem("branchName");
      dispatch(branchNameSet(null));
      dispatch(modeSet("light"));
      localStorage.setItem("colorMode", "light");
      dispatch(authSet(undefined));
      dispatch(logout());
      setOpen(false);
      navigate("/login");
    } catch (error) {
      console.log(error);
    }
  };

  const renderContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        paddingY: "16px",
      }}
    >
      <Typography sx={{ fontSize: "40px", lineHeight: 1 }}>🎉</Typography>
      <Typography sx={{ fontSize: "20px", fontWeight: 700, textAlign: "center" }}>
        Your New Dashboard Is Ready!
      </Typography>
      <Typography sx={{ fontSize: "15px", textAlign: "center", color: "#64748B" }}>
        Great news — your dealership now has access to Dashboard V2, a faster and smarter experience
        built for you. Let&apos;s take you there!
      </Typography>
    </Box>
  );

  const renderActions = (
    <Box display={"flex"} justifyContent={"center"} gap={"12px"} width={"100%"}>
      <Button
        onClick={redirectToDashboardV2}
        color="primary"
        variant="contained"
        sx={{
          background: "linear-gradient(101deg, #3B82F6 -1.78%, #9333EA 108.45%)",
          color: "white",
          fontSize: "15px",
          fontWeight: 700,
          textTransform: "initial",
          paddingX: "32px",
          "&:hover": {
            backgroundColor: "#2B7FFF",
            boxShadow: "none",
          },
        }}
      >
        Go To My New Dashboard
      </Button>

      <Button
        onClick={handleLogout}
        variant="outlined"
        sx={{
          color: "#64748B",
          borderColor: "#CBD5E1",
          fontSize: "15px",
          fontWeight: 600,
          textTransform: "initial",
          paddingX: "24px",
          "&:hover": {
            borderColor: "#94A3B8",
            backgroundColor: "transparent",
          },
        }}
      >
        Log Out
      </Button>
    </Box>
  );

  return (
    <Dialog
      open={open}
      fullWidth
      // No close icon and a no-op close handler: backdrop clicks and Escape
      // must not dismiss this modal.
      showCloseButton={false}
      handleClose={() => undefined}
      renderContent={renderContent}
      renderActions={renderActions}
    />
  );
}
